import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { useUserGroup } from '../../hooks/useUserGroup';
import { landingIsV2, V2_STUDIO_PATH } from '../../lib/userGroups';
import type { Tramo } from '../../lib/onboardingMarca/dialogo';
import { leerMarca, type MarcaGuardada } from '../../lib/onboardingMarca/marca';
import { leerEstado, limpiarEstado } from '../../lib/onboardingMarca/estado';
import { DESCRIBIR_MARCA, enDescribirMarca, enMarcaNueva, MARCA_NUEVA } from '../../lib/onboardingMarca/marcaNueva';
import { yaDescargo } from '../../lib/onboardingMarca/descargaDelOnboarding';
import { abreElCatalogoDePlanes } from '../../lib/cuestionarioSegunPlan';
import { SetupPricingSheet } from '../../components/pricing/SetupPricingSheet';

/** Lo que tarda, como mucho, la marca en llegar antes de que Cumbre hable igual. */
const ESPERA_MAX_MARCA_MS = 1500;

/** Los colores del confeti cuando la marca no trae paleta: los de Atlas. */
const COLORES_DE_ATLAS = ['#1644EB', '#60E7F0', '#02062B', '#FFC93C', '#FF6B4A'];
const PAPELITOS = 26;

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function dialogoDelFinal(nombre: string, conPiezas: boolean, nueva = false): Tramo[] {
  return [
    { texto: '¡Listo!', estilo: 'saludo' },
    { texto: `${nombre} ya está en Atlas`, estilo: 'pregunta' },
    {
      texto: nueva
        ? 'Ya es tu marca activa: lo que crees desde ahora usa su logo, sus colores y su forma de comunicar. Cambias de marca desde el selector.'
        : conPiezas
        ? 'Todo lo que crees desde ahora usa tu logo, tus colores y tu forma de comunicar. Tus primeras piezas quedaron en Creaciones.'
        : 'Todo lo que crees desde ahora usa tu logo, tus colores y tu forma de comunicar.',
      estilo: 'detalle',
    },
  ];
}

/**
 * A6 · Listo (ADR-2026-09-28-onboarding-v2-etapa-1): la marca armada, las piezas que
 * salieron y la entrada a Atlas. Al entrar se limpia lo que el onboarding recordaba.
 *
 * Con Cumbre (ADR-2026-10-02-onboarding-v2-listo-con-cumbre): es el cierre, así que festeja.
 * Dice que la marca ya está en Atlas, tira confeti con los colores de la marca y muestra su
 * credencial (logo, nombre, rubro, paleta y las primeras piezas).
 */
export function MarcaLista() {
  const navigate = useNavigate();
  const location = useLocation();
  // "Añadir nueva marca" termina acá también (ADR-2026-10-02-marca-nueva-como-el-onboarding).
  const nueva = enMarcaNueva(location.pathname);
  // "Describí tu marca" desde la home (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo)
  // termina igual que la marca nueva: al estudio, sin catálogo de planes.
  const describir = enDescribirMarca(location.pathname);
  const { isDevTester, grupo, plan } = useUserGroup();
  const [conPlanes, setConPlanes] = useState(false);
  const inicial = useMemo(() => leerEstado(null), []);
  const [marca, setMarca] = useState<MarcaGuardada | null>(null);
  const [esperoMarca, setEsperoMarca] = useState(true);
  const piezas = ((location.state as { piezas?: Array<{ etiqueta: string; url: string }> } | null)?.piezas ?? []).slice(0, 3);
  const [quieto] = useState(pideMenosMovimiento);

  useEffect(() => {
    if (!inicial?.brandId) {
      navigate(nueva ? MARCA_NUEVA.link : describir ? DESCRIBIR_MARCA.link : ONBOARDING_V2.setup, { replace: true });
      return;
    }
    let vivo = true;
    leerMarca(inicial.brandId)
      .then((m) => vivo && setMarca(m))
      .finally(() => vivo && setEsperoMarca(false));
    // Si la marca tarda, Cumbre habla igual (con "Tu marca").
    const t = window.setTimeout(() => vivo && setEsperoMarca(false), ESPERA_MAX_MARCA_MS);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [inicial?.brandId, navigate, nueva, describir]);

  const nombre = marca?.nombre || 'Tu marca';

  // ── Cumbre ── (aparece cuando ya sabe el nombre: si no, reescribiría el globo)
  const tramos = useMemo(() => dialogoDelFinal(nombre, piezas.length > 0, nueva), [nombre, piezas.length, nueva]);
  // La cuenta para empezar a hablar arranca cuando Cumbre aparece (con la marca), no al montar.
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: esperoMarca ? 60_000 : 900 });
  const [mostrar, setMostrar] = useState(quieto);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const [confeti, setConfeti] = useState(false);
  useEffect(() => {
    if (!dialogo.terminado || esperoMarca) return;
    setMostrar(true);
    // El festejo: Cumbre salta y cae confeti, una sola vez.
    setGesto((g) => g ?? { tipo: 'festeja', n: 1 });
    if (!quieto) setConfeti(true);
  }, [dialogo.terminado, esperoMarca, quieto]);

  const colores = marca?.colores.map((c) => c.hex).filter((h) => /^#[0-9a-f]{6}$/i.test(h)) ?? [];
  const paletaDelConfeti = colores.length >= 2 ? colores : COLORES_DE_ATLAS;
  const papelitos = useMemo(
    () =>
      Array.from({ length: PAPELITOS }, (_, i) => ({
        x: (i * 37) % 100,
        demora: (i % 7) * 0.09,
        giro: (i * 53) % 360,
        duracion: 1.6 + ((i * 17) % 10) / 10,
        ancho: 6 + (i % 3) * 3,
      })),
    [],
  );

  /** Sale del onboarding: a la marca nueva activa en el estudio, o a la home del grupo. */
  const salir = () => {
    limpiarEstado();
    // Una marca nueva entra al estudio, ya activa (como `RevisarMarca`). La descrita desde la
    // home también: `marcaRecienCompletada` da el paso de la tarjeta por hecho.
    if (nueva || describir) {
      navigate(V2_STUDIO_PATH, { replace: true, state: { marcaRecienCompletada: true } });
      return;
    }
    navigate(landingIsV2(isDevTester, grupo) ? '/interfaz-v2' : '/redes-sociales/crear-anuncio/seleccionar-productos', {
      replace: true,
    });
  };

  /**
   * "Entrar a Atlas". Si se llevó alguna de sus primeras piezas (y no tiene plan pago), antes
   * ve el catálogo de planes, como al salir del cuestionario de siempre
   * (`abreElCatalogoDePlanes`, ADR-2026-10-05-onboarding-v2-descarga-abre-planes). Cerrarlo
   * sigue a Atlas; "Suscribirme" va a pagar (ADR-2026-10-20-suscribirme-del-onboarding-cobra).
   */
  const entrar = () => {
    if (!nueva && !describir && abreElCatalogoDePlanes({ plan, descargo: yaDescargo() })) {
      setConPlanes(true);
      return;
    }
    salir();
  };

  return (
    <div className="relative isolate flex min-h-[calc(100dvh-var(--om-barra))] flex-col">
      <FondoCordillera quieto={quieto} />

      {confeti && (
        <div className="om-confeti" aria-hidden="true">
          {papelitos.map((p, i) => (
            <span
              key={i}
              style={{
                left: `${p.x}%`,
                width: p.ancho,
                background: paletaDelConfeti[i % paletaDelConfeti.length],
                animationDelay: `${p.demora}s`,
                animationDuration: `${p.duracion}s`,
                ['--giro' as string]: `${p.giro}deg`,
              }}
            />
          ))}
        </div>
      )}

      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5 px-4 pb-10 pt-6 md:gap-6 md:pt-8">
        {!esperoMarca && (
          <GuiaCumbre
            tramos={tramos}
            letras={dialogo.letras}
            hablando={dialogo.hablando}
            terminado={dialogo.terminado}
            onCompletar={dialogo.completar}
            reaccion={null}
            animo="escucha"
            gesto={gesto}
            instantaneo={quieto}
          />
        )}

        {mostrar && (
          <section className="om-seccion om-credencial" aria-label={`La marca ${nombre}`}>
            <header className="flex items-center gap-4">
              <span className="om-damero om-credencial__logo">
                {marca?.logoUrl ? (
                  <img src={marca.logoUrl} alt={`Logo de ${nombre}`} className="max-h-12 max-w-12 object-contain" />
                ) : (
                  <span className="text-[24px] font-extrabold text-[#02062B]">{nombre.slice(0, 1)}</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-[#02062B]">{nombre}</p>
                {marca?.rubro && <span className="om-credencial__rubro">{marca.rubro}</span>}
              </div>
              <span className="om-credencial__sello" aria-hidden="true">
                <span className="material-symbols-rounded">verified</span>
              </span>
            </header>

            {marca && colores.length > 0 && (
              <div className="om-paleta" aria-label="Tu paleta">
                {marca.colores.slice(0, 5).map((c, i) => (
                  <span key={`${c.hex}-${i}`} style={{ background: c.hex }} title={c.name || c.hex} />
                ))}
              </div>
            )}

            {piezas.length > 0 && (
              <div>
                <p className="mb-2.5 text-[13px] font-bold text-[#4B5275]">Tus primeras piezas</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {piezas.map((p, i) => (
                    <img
                      key={p.url}
                      src={p.url}
                      alt={p.etiqueta}
                      className="om-credencial__pieza"
                      style={{ ['--i' as string]: i }}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {mostrar && (
        <div className="om-barra">
          <div className="om-barra__dentro">
            <button type="button" onClick={entrar} className="om-boton md:w-auto md:px-12">
              {nueva || describir ? `Crear con ${nombre}` : 'Entrar a Atlas'}
            </button>
          </div>
        </div>
      )}

      {conPlanes && (
        <SetupPricingSheet
          onDismiss={() => {
            setConPlanes(false);
            salir();
          }}
          // Igual que `salir`: lo que el onboarding recordaba se olvida antes de irse,
          // así al volver de pagar no retoma el onboarding.
          alIrAPagar={limpiarEstado}
        />
      )}
    </div>
  );
}

export default MarcaLista;
